import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Form } from "antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { WHITE, SUCCESS_600 } from "../../constant/colors";
import { SettingsIcon } from "../../assets/svg/SettingsIcon";
import PhysicianIcon from "../../assets/svg/PhysicianIcon";
import {
  selectProvidersAllDoctors,
  selectIsDoctorExactDuplicate,
  selectUser,
  selectEnvironment,
  selectUsers
} from "../../store/selector";
import ACTIONS from "../../store/action";
import { PhysicianForm } from "./PhysicianForm";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import { RouterPrompt } from "../../router/RouterPrompt";
import { trackPendoEvent } from "../../utils/pendo/pendoUtils";
import { getNamesFromFullName } from "../../component/Patient/ApplicationFormEditor/utils/parsers";
import {
  BreadcrumbSection,
  BreadcrumbLeft,
  BreadcrumbItem,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  ButtonRow,
  StyledPrimaryButton,
  CancelButton,
  MetaText,
  CreatedText,
  UpdatedText
} from "./shared/components";
import {
  handleSettingsClick as sharedHandleSettingsClick,
  handlePhysiciansClick as sharedHandlePhysiciansClick
} from "./shared/handlers";
import { dateTimeFormatter } from "../../utils/date";
import { getUserName } from "./shared/utils";

const PhysicianFormPageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 12px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  overflow-y: auto;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  padding: 8px 4px;
  margin-bottom: 16px;
`;

const StatusToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 8px;
  border-radius: 20px;
  background-color: ${(props) => (props.isActive ? SUCCESS_600 : "#9e9e9e")};
  cursor: pointer;
  transition: background-color 0.3s ease;
  position: relative;
  min-width: 85px;

  &:hover {
    background-color: ${(props) => (props.isActive ? "#27632a" : "#757575")};
  }
`;

const StatusLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: white;
  position: relative;
  z-index: 1;
  margin-left: ${(props) => (props.isActive ? "4px" : "16px")};
  margin-right: ${(props) => (props.isActive ? "16px" : "4px")};
`;

const ToggleCircle = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: white;
  position: absolute;
  left: ${(props) => (props.isActive ? "calc(100% - 20px)" : "5px")};
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const PhysicianFormPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const physicians = useSelector(selectProvidersAllDoctors) || [];
  const isExactDuplicate = useSelector(selectIsDoctorExactDuplicate);
  const users = useSelector(selectUsers) || {};
  const formValues = Form.useWatch([], form);
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);

  const isEdit = !!id;
  const physician = isEdit ? physicians.find((p) => p.id === parseInt(id)) : null;
  const [isActive, setIsActive] = React.useState(physician?.isActive !== false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(isEdit);
  const [isSaving, setIsSaving] = React.useState(false);
  const [popupDescription, setPopupDescription] = React.useState(t("physician_management.modal.unsaved_message"));
  const [hasValidationErrors, setHasValidationErrors] = React.useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = React.useState(null);
  const errorOccurredRef = React.useRef(false);

  // Fetch physicians on mount
  useEffect(() => {
    if (isEdit && physicians.length === 0) {
      dispatch(ACTIONS.providersAction("doctor"));
    }
  }, [isEdit, dispatch, physicians.length]);

  // Check if physician exists after data is loaded
  useEffect(() => {
    if (isEdit) {
      if (physicians.length > 0 && !physician) {
        // Physician data loaded but physician not found - redirect
        history.push("/physicians-settings");
      } else if (physicians.length > 0) {
        // Data loaded and physician found
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [isEdit, physician, physicians.length, history]);

  useEffect(() => {
    // Clear duplicates when component mounts
    dispatch(ACTIONS.setDoctorDuplicates([]));
    dispatch(ACTIONS.setDoctorExactDuplicate(false));
  }, [dispatch]);

  // Track initial values
  useEffect(() => {
    if (isEdit && physician && !initialValues) {
      const { firstName, lastName } = getNamesFromFullName(physician.name);
      // Exclude 'name' from physician object since we're using firstName/lastName instead
      // eslint-disable-next-line no-unused-vars
      const { name, ...physicianWithoutName } = physician;
      const initial = {
        ...physicianWithoutName,
        firstName,
        lastName,
        isActive: physician.isActive
      };
      setInitialValues(initial);
      setHasUnsavedChanges(false);
    } else if (!isEdit && !initialValues) {
      // For new physicians, set empty initial values
      setInitialValues({});
      setHasUnsavedChanges(false);
    }
  }, [physician, initialValues, isEdit]);

  // Track form changes
  useEffect(() => {
    // Don't track changes until initial values are set
    if (initialValues === null) {
      return;
    }

    if (!formValues) {
      setHasUnsavedChanges(false);
      return;
    }

    if (!isEdit) {
      // For new physicians, check if any field has been filled with a non-empty value
      const hasAnyValue = Object.values(formValues).some((value) => {
        if (typeof value === "string") {
          return value.trim().length > 0;
        }
        return value !== undefined && value !== null;
      });
      setHasUnsavedChanges(hasAnyValue);
    } else {
      // For edits, compare each field individually
      const currentFormValues = { ...formValues };
      const changes = Object.keys(currentFormValues).filter((key) => {
        const formValue = currentFormValues[key];
        const initialValue = initialValues[key];

        // Normalize empty values
        const normalizedFormValue = formValue === "" || formValue === undefined ? null : formValue;
        const normalizedInitialValue = initialValue === "" || initialValue === undefined ? null : initialValue;

        return normalizedFormValue !== normalizedInitialValue;
      });

      const hasChanges = changes.length > 0 || isActive !== initialValues.isActive;
      setHasUnsavedChanges(hasChanges);
    }
  }, [formValues, initialValues, isEdit, isActive]);

  // Reset popup description when there are no unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) {
      setPopupDescription(t("physician_management.modal.unsaved_message"));
    }
  }, [hasUnsavedChanges, t]);

  // Post-save navigation must wait for the re-render that disarms RouterPrompt's
  // history.block guard — React 19 batches the setHasUnsavedChanges(false) update,
  // so a synchronous history.push would still hit the guard armed with the stale value
  useEffect(() => {
    if (pendingNavigationPath && !hasUnsavedChanges) {
      history.push(pendingNavigationPath);
    }
  }, [pendingNavigationPath, hasUnsavedChanges, history]);

  // Handler for form field changes - checks validation errors in real-time
  const handleFieldsChange = React.useCallback((changedFields, allFields) => {
    // Check if any field has errors
    const hasErrors = allFields.some((field) => field.errors && field.errors.length > 0);
    setHasValidationErrors(hasErrors);
  }, []);

  const handleDuplicateViewClick = React.useCallback(() => {
    // Set the redirect-specific message for the unsaved changes popup
    // The navigation is deferred in PhysicianForm to allow this state update to process first
    setPopupDescription(t("cancel_modal_description_physician_duplicate_redirect"));
  }, [t]);

  const handleSettingsClick = () => sharedHandleSettingsClick({ dispatch, user, account, history });

  const handlePhysiciansClick = () => sharedHandlePhysiciansClick({ dispatch, user, account, history });

  const handleToggleStatus = () => {
    const newStatus = !isActive;
    try {
      trackPendoEvent(dispatch, newStatus ? "Physician_Toggle_Active" : "Physician_Toggle_Inactive", {
        username: user?.email,
        account,
        physicianId: physician?.id
      });
    } catch (error) {
      console.error("Error dispatching physician toggle Pendo event:", error);
    }
    setIsActive(newStatus);
  };

  const handleCancel = () => {
    try {
      trackPendoEvent(dispatch, "Physician_Cancel", {
        username: user?.email,
        account,
        physicianId: physician?.id,
        isEdit
      });
    } catch (error) {
      console.error("Error dispatching physician cancel Pendo event:", error);
    }
    if (id) {
      // If editing, go to detail page
      history.push(`/physicians-settings/${id}`);
    } else {
      // If adding new, go to listing page
      history.push("/physicians-settings");
    }
  };

  const handleSubmit = () => {
    try {
      trackPendoEvent(dispatch, "Physician_Save", {
        username: user?.email,
        account,
        physicianId: physician?.id,
        isEdit
      });
    } catch (error) {
      console.error("Error dispatching physician save Pendo event:", error);
    }

    form
      .validateFields()
      .then((values) => {
        // Don't save if there's an exact duplicate (NPI + Address match)
        // NPI-only duplicates show a warning but allow saving
        if (isExactDuplicate) {
          return;
        }

        setIsSaving(true);
        // Reset error flag before new submission
        errorOccurredRef.current = false;

        // Combine firstName and lastName into name in "lastName, firstName" format
        const { firstName, lastName, ...restValues } = values;
        const name = lastName && firstName ? `${lastName}, ${firstName}` : firstName || lastName || "";

        const physicianData = {
          ...restValues,
          name,
          isActive
        };

        if (isEdit) {
          dispatch(
            ACTIONS.updatePhysicianAction(
              physician.id,
              physicianData,
              () => {
                // Only proceed with success handling if no error occurred
                if (errorOccurredRef.current) return;

                setIsSaving(false);
                setHasUnsavedChanges(false);
                SuccessMessage(t("physician_management.form.success_updated"));
                setPendingNavigationPath("/physicians-settings");
              },
              (error) => {
                errorOccurredRef.current = true;
                setIsSaving(false);
                console.error("Error updating physician:", error);
              }
            )
          );
        } else {
          dispatch(
            ACTIONS.createPhysicianAction(
              physicianData,
              (response) => {
                // Only proceed with success handling if no error occurred
                if (errorOccurredRef.current) return;

                setIsSaving(false);
                setHasUnsavedChanges(false);
                SuccessMessage(t("physician_management.form.success_created"));
                // The saga extracts the provider from the providers array
                const physicianId = response?.id;
                if (physicianId) {
                  setPendingNavigationPath(`/physicians-settings/${physicianId}`);
                } else {
                  console.error("No physician ID in response:", response);
                  setPendingNavigationPath("/physicians-settings");
                }
              },
              (error) => {
                errorOccurredRef.current = true;
                setIsSaving(false);
                console.error("Error creating physician:", error);
              }
            )
          );
        }
      })
      .catch(() => {
        // Silently handle validation errors - they're shown inline on the form
      });
  };

  if (isLoading || (isEdit && !initialValues)) {
    return null; // or a loading spinner
  }

  return (
    <>
      <RouterPrompt
        shouldTriggerPopup={hasUnsavedChanges}
        popupDescription={popupDescription}
        popupTitle={t("physician_management.modal.unsaved_title")}
        okText={t("yes_sure")}
        usePrimaryButton
      />
      <PhysicianFormPageContainer>
        <HeaderSection>
          <BreadcrumbSection>
            <BreadcrumbLeft>
              <BreadcrumbItem id="Physician_Breadcrumbs_Settings" onClick={handleSettingsClick}>
                <SettingsIcon />
                {t("physician_management.breadcrumb.settings")}
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem id="Physician_Breadcrumbs_Physicians" onClick={handlePhysiciansClick}>
                <PhysicianIcon />
                {t("physician_management.breadcrumb.physicians")}
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbCurrent>
                {isEdit ? physician?.name : t("physician_management.breadcrumb.new_physician")}
              </BreadcrumbCurrent>
              <StatusToggle
                id={isActive ? "Physician_Toggle_Active" : "Physician_Toggle_Inactive"}
                isActive={isActive}
                onClick={handleToggleStatus}
                className="status-toggle"
              >
                <ToggleCircle isActive={isActive} />
                <StatusLabel isActive={isActive}>
                  {isActive
                    ? t("physician_management.form_page.status_active")
                    : t("physician_management.form_page.status_inactive")}
                </StatusLabel>
              </StatusToggle>
            </BreadcrumbLeft>
            <ButtonRow>
              <CancelButton id="Physician_Cancel" onClick={handleCancel}>
                {t("physician_management.form_page.button_cancel")}
              </CancelButton>
              <StyledPrimaryButton
                id="Physician_Save"
                type="primary"
                onClick={handleSubmit}
                disabled={!hasUnsavedChanges || isExactDuplicate || hasValidationErrors}
                loading={isSaving}
              >
                {isSaving
                  ? t("physician_management.form_page.button_saving")
                  : t("physician_management.form_page.button_save")}
              </StyledPrimaryButton>
            </ButtonRow>
          </BreadcrumbSection>
          {id && (
            <MetaText>
              <CreatedText>
                {t("physician_management.detail.created_by", {
                  user: getUserName(users, physician.createdBy, t("physician_management.detail.default_user")),
                  date: dateTimeFormatter(physician.createdAt)
                })}
              </CreatedText>
              {physician.updatedBy && physician.userUpdatedAt && (
                <UpdatedText>
                  {t("physician_management.detail.updated_by", {
                    user: getUserName(users, physician.updatedBy, t("physician_management.detail.default_user")),
                    date: dateTimeFormatter(physician.userUpdatedAt)
                  })}
                </UpdatedText>
              )}
            </MetaText>
          )}
        </HeaderSection>

        <PhysicianForm
          form={form}
          initialValues={initialValues}
          isEdit={isEdit}
          onDuplicateViewClick={handleDuplicateViewClick}
          onFieldsChange={handleFieldsChange}
        />
      </PhysicianFormPageContainer>
    </>
  );
};

export { PhysicianFormPage };
